
import React, { useRef, useEffect, useState } from 'react';
import ChatMessage from './ChatMessage';
import { Message } from '@/types/insurance';
import ChatTemplateChange from './ChatTemplateChange';

interface ChatMessagesAreaProps {
  messages: Message[];
  isStreaming: boolean;
  templateChangeDescription?: string | null;
  templateChangeRequest?: string | null;
  templateChangeConfirmed?: boolean;
  isTemplateGenerating?: boolean;
  confirmAndProcessTemplateChange?: () => void;
  cancelTemplateChange?: () => void;
  onEmailGenerated: (content: string) => void;
  onConceptConfirmed?: (concept: string) => void;
  templateChangeRequiresConfirmation?: boolean;
  handleConfirmTemplateChange?: (messageId: string, content: string) => void;
  extractedTemplates?: Map<string, string>;
  isAtBottom?: boolean;
  scrollToBottom?: () => void;
  onOpenOfferForm?: () => void;
  onOpenComparisonForm?: () => void;
  onRequestIdeas?: (context: string) => void;
  triggerTemplateGeneration?: (concept: string) => Promise<void>;
}

const ChatMessagesArea: React.FC<ChatMessagesAreaProps> = ({
  messages,
  isStreaming,
  templateChangeDescription,
  templateChangeRequest,
  templateChangeConfirmed,
  isTemplateGenerating = false,
  confirmAndProcessTemplateChange,
  cancelTemplateChange,
  onEmailGenerated,
  onConceptConfirmed,
  templateChangeRequiresConfirmation,
  handleConfirmTemplateChange,
  extractedTemplates,
  isAtBottom,
  scrollToBottom,
  onOpenOfferForm,
  onOpenComparisonForm,
  onRequestIdeas,
  triggerTemplateGeneration
}) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [pendingTemplateGeneration, setPendingTemplateGeneration] = useState<boolean>(false);

  useEffect(() => {
    if (scrollToBottom && (messages.length > 0 || isStreaming)) {
      scrollToBottom();
    }
  }, [messages.length, isStreaming, scrollToBottom]);

  useEffect(() => {
    const handleTemplateGenerationStarted = () => {
      console.log("[DEBUG] ChatMessagesArea - Template generation started event received");
      setPendingTemplateGeneration(true);
    };
    
    const handleTemplateGenerationComplete = () => {
      console.log("[DEBUG] ChatMessagesArea - Template generation complete event received");
      setPendingTemplateGeneration(false);
    };
    
    const handleTemplateGenerationFailed = () => {
      console.log("[DEBUG] ChatMessagesArea - Template generation failed event received");
      setPendingTemplateGeneration(false);
    };
    
    window.addEventListener('template-generation-started', handleTemplateGenerationStarted as EventListener);
    window.addEventListener('template-generation-complete', handleTemplateGenerationComplete as EventListener);
    window.addEventListener('template-generation-failed', handleTemplateGenerationFailed as EventListener);
    
    return () => {
      window.removeEventListener('template-generation-started', handleTemplateGenerationStarted as EventListener);
      window.removeEventListener('template-generation-complete', handleTemplateGenerationComplete as EventListener);
      window.removeEventListener('template-generation-failed', handleTemplateGenerationFailed as EventListener);
    };
  }, []);

  const handleScroll = () => {
    if (!scrollAreaRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollAreaRef.current;
    const isBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 10;
    
    if (isBottom && scrollToBottom) {
      scrollToBottom();
    }
  };

  console.log("Rendering messages:", messages.length, "with the following data:", messages.map(m => ({
    id: m.id,
    role: m.role,
    metadata: m.metadata,
    content: m.content ? m.content.substring(0, 20) + "..." : "No content"
  })));

  return (
    <div className="flex-1 overflow-y-auto p-4 bg-black" ref={scrollAreaRef} onScroll={handleScroll}>
      <div className="space-y-4">
        {messages.map((message) => {
          // Ensure message has a required id property
          if (!message.id) {
            console.warn("Message without ID found:", message);
            // Generate a temporary ID for any message that doesn't have one
            const tempMessage = { 
              ...message, 
              id: `temp-${Math.random().toString(36).substring(2, 15)}`,
              role: message.role || 'assistant'
            };
            
            return (
              <ChatMessage
                key={tempMessage.id}
                message={tempMessage as any}
                onConceptConfirmed={onConceptConfirmed}
                requiresConfirmation={templateChangeRequiresConfirmation}
                onConfirmChanges={handleConfirmTemplateChange}
                extractedTemplate={extractedTemplates?.get(tempMessage.id)}
                onEmailGenerated={onEmailGenerated}
                onOpenOfferForm={onOpenOfferForm}
                onOpenComparisonForm={onOpenComparisonForm}
                onRequestIdeas={onRequestIdeas}
                triggerTemplateGeneration={triggerTemplateGeneration}
              />
            );
          }
          
          // Standardize message role to ensure proper styling
          let role = message.role;
          if (role !== 'user' && role !== 'assistant' && role !== 'system') {
            role = 'assistant';
          }
          
          // Enhance metadata if missing to ensure proper button display
          const enhancedMetadata = message.metadata || {};
          if (!message.metadata && !message.isEmail && role === 'assistant') {
            if (message.content && message.content.includes('concept') && !message.content.includes('<html')) {
              enhancedMetadata.emailStage = 'concept';
            }
          }
          
          console.log(`Rendering message ${message.id} with role: ${role}, metadata:`, enhancedMetadata);

          // Create an enhanced message with standardized properties
          const enhancedMessage = {
            ...message,
            role: role as "user" | "assistant" | "system",
            metadata: enhancedMetadata,
            id: message.id
          };
          
          return (
            <ChatMessage
              key={enhancedMessage.id}
              message={enhancedMessage as any}
              onConceptConfirmed={onConceptConfirmed}
              requiresConfirmation={templateChangeRequiresConfirmation}
              onConfirmChanges={handleConfirmTemplateChange}
              extractedTemplate={extractedTemplates?.get(enhancedMessage.id)}
              onEmailGenerated={onEmailGenerated}
              onOpenOfferForm={onOpenOfferForm}
              onOpenComparisonForm={onOpenComparisonForm}
              onRequestIdeas={onRequestIdeas}
              triggerTemplateGeneration={triggerTemplateGeneration}
            />
          );
        })}
        
        {templateChangeRequest && templateChangeDescription && !templateChangeConfirmed && (
          <ChatTemplateChange
            description={templateChangeDescription}
            isGenerating={isTemplateGenerating || false}
            onConfirm={confirmAndProcessTemplateChange || (() => {})}
            onCancel={cancelTemplateChange || (() => {})}
          />
        )}
        
        {(isStreaming || pendingTemplateGeneration) && (
          <div className="flex items-center space-x-2 p-3 bg-gray-800/40 rounded-lg">
            <div className="animate-pulse flex gap-1">
              <div className="h-2 w-2 bg-jade rounded-full animate-bounce"></div>
              <div className="h-2 w-2 bg-jade rounded-full animate-bounce [animation-delay:0.2s]"></div>
              <div className="h-2 w-2 bg-jade rounded-full animate-bounce [animation-delay:0.4s]"></div>
            </div>
            <span className="text-gray-400 text-sm">
              {pendingTemplateGeneration ? "Generiere Template..." : "Generiere Antwort..."}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessagesArea;
